import { projectStats } from "../data/project-stats";
import { createViewportRendererLifecycle, type ViewportRendererLifecycle } from "../experience/viewport-renderer";
import { deferRendererBoot } from "../experience/defer-renderer";
const hosts = document.querySelectorAll<HTMLElement>("[data-construction-field]");
const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
const fine = matchMedia("(pointer:fine)").matches;
const boot = async () => {
	if (!hosts.length) return;
	const THREE = await import("three");
	hosts.forEach((host) => {
		const canvas = host.querySelector<HTMLCanvasElement>("canvas");
		if (!canvas) return;
		const renderer = new THREE.WebGLRenderer({
			canvas,
			alpha: true,
			antialias: false,
			depth: false,
			stencil: false,
			precision: "mediump",
			powerPreference: "high-performance"
		});
		renderer.setPixelRatio(Math.min(devicePixelRatio, 1));
		const scene = new THREE.Scene();
		const camera = new THREE.PerspectiveCamera(34, 1, .1, 100);
		camera.position.set(6.28, 5.12, 8.36);
		camera.lookAt(0, .72, 0);
		const root = new THREE.Group();
		const house = new THREE.Group();
		scene.add(root);
		root.add(house);
		root.position.set(.16, .02, .12);
		root.rotation.set(-.035, .06, 0);
		house.scale.set(1.08, 1.08, 1.08);
		const gridMat = new THREE.LineBasicMaterial({
			color: 5992344,
			transparent: true,
			opacity: .28
		});
		const softMat = new THREE.LineBasicMaterial({
			color: 7441091,
			transparent: true,
			opacity: .34
		});
		const accentMat = new THREE.LineBasicMaterial({
			color: 7903464,
			transparent: true,
			opacity: .86
		});
		const gridGeo = new THREE.BufferGeometry();
		const grid: number[] = [];
		for (let i = -6; i <= 6; i++) {
			grid.push(-6, 0, i, 6, 0, i, i, 0, -6, i, 0, 6);
		}
		gridGeo.setAttribute("position", new THREE.Float32BufferAttribute(grid, 3));
		root.add(new THREE.LineSegments(gridGeo, gridMat));
		const houseVerts = [
			-2,
			0,
			-1.4,
			2,
			0,
			-1.4,
			2,
			0,
			-1.4,
			2,
			0,
			1.4,
			2,
			0,
			1.4,
			-2,
			0,
			1.4,
			-2,
			0,
			1.4,
			-2,
			0,
			-1.4,
			-2,
			0,
			-1.4,
			-2,
			2,
			-1.4,
			2,
			0,
			-1.4,
			2,
			2,
			-1.4,
			2,
			0,
			1.4,
			2,
			2,
			1.4,
			-2,
			0,
			1.4,
			-2,
			2,
			1.4,
			-2,
			2,
			-1.4,
			2,
			2,
			-1.4,
			2,
			2,
			-1.4,
			2,
			2,
			1.4,
			2,
			2,
			1.4,
			-2,
			2,
			1.4,
			-2,
			2,
			1.4,
			-2,
			2,
			-1.4,
			-2,
			2,
			-1.4,
			0,
			3,
			-1.4,
			0,
			3,
			-1.4,
			2,
			2,
			-1.4,
			-2,
			2,
			1.4,
			0,
			3,
			1.4,
			0,
			3,
			1.4,
			2,
			2,
			1.4,
			0,
			3,
			-1.4,
			0,
			3,
			1.4
		];
		const houseGeo = new THREE.BufferGeometry();
		houseGeo.setAttribute("position", new THREE.Float32BufferAttribute(houseVerts, 3));
		house.add(new THREE.LineSegments(houseGeo, accentMat));
		const floorGeo = new THREE.BufferGeometry();
		floorGeo.setAttribute("position", new THREE.Float32BufferAttribute([
			-2,
			.03,
			-1.4,
			2,
			.03,
			-1.4,
			2,
			.03,
			-1.4,
			2,
			.03,
			1.4,
			2,
			.03,
			1.4,
			-2,
			.03,
			1.4,
			-2,
			.03,
			1.4,
			-2,
			.03,
			-1.4
		], 3));
		house.add(new THREE.LineSegments(floorGeo, softMat));
		const ringGeo = new THREE.BufferGeometry();
		const ring: number[] = [];
		for (let i = 0; i < 96; i++) {
			const a = i / 96 * Math.PI * 2, b = (i + 1) / 96 * Math.PI * 2;
			ring.push(Math.cos(a) * 4, .09, Math.sin(a) * 4, Math.cos(b) * 4, .09, Math.sin(b) * 4);
		}
		ringGeo.setAttribute("position", new THREE.Float32BufferAttribute(ring, 3));
		root.add(new THREE.LineSegments(ringGeo, softMat));
		const dots = new THREE.BufferGeometry();
		const positions: number[] = [];
		for (let i = 0; i < projectStats.macroTasks; i++) {
			const a = i / projectStats.macroTasks * Math.PI * 2;
			const r = 3.25 + i % 4 * .11;
			positions.push(Math.cos(a) * r, .12 + i % 5 * .12, Math.sin(a) * r);
		}
		dots.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
		root.add(new THREE.Points(dots, new THREE.PointsMaterial({
			color: 9546495,
			size: .048,
			transparent: true,
			opacity: .72
		})));
		let targetX = -.035, targetY = .06, targetCameraX = 6.28, targetCameraY = 5.12;
		let hostRect: DOMRect | null = null;
		let lifecycle: ViewportRendererLifecycle | null = null;
		const resize = () => {
			hostRect = host.getBoundingClientRect();
			const r = hostRect;
			renderer.setSize(Math.max(1, r.width), Math.max(1, r.height), false);
			camera.aspect = Math.max(1, r.width) / Math.max(1, r.height);
			camera.updateProjectionMatrix();
			lifecycle?.invalidate();
		};
		const ro = new ResizeObserver(resize);
		resize();
		ro.observe(host);
		const enter = () => {
			hostRect = host.getBoundingClientRect();
		};
		const pointer = (event: PointerEvent) => {
			if (!fine || reduced || !hostRect) return;
			const nx = (event.clientX - hostRect.left) / Math.max(1, hostRect.width) - .5;
			const ny = (event.clientY - hostRect.top) / Math.max(1, hostRect.height) - .5;
			targetY = .06 + nx * .32;
			targetX = -.035 + ny * .14;
			targetCameraX = 6.28 + nx * .28;
			targetCameraY = 5.12 - ny * .18;
			lifecycle?.invalidate();
		};
		const leave = () => {
			targetY = .06;
			targetX = -.035;
			targetCameraX = 6.28;
			targetCameraY = 5.12;
			lifecycle?.invalidate();
		};
		host.addEventListener("pointerenter", enter, { passive: true });
		host.addEventListener("pointermove", pointer, { passive: true });
		host.addEventListener("pointerleave", leave, { passive: true });
		const renderFrame = (_time: number, animate: boolean) => {
			let settling = false;
			if (animate) {
				const dy = targetY - root.rotation.y, dx = targetX - root.rotation.x, dcx = targetCameraX - camera.position.x, dcy = targetCameraY - camera.position.y;
				root.rotation.y += dy * .22;
				root.rotation.x += dx * .22;
				camera.position.x += dcx * .2;
				camera.position.y += dcy * .2;
				camera.lookAt(0, .9, 0);
				settling = Math.abs(dy) + Math.abs(dx) + Math.abs(dcx) + Math.abs(dcy) > .006;
			}
			renderer.render(scene, camera);
			return settling;
		};
		lifecycle = createViewportRendererLifecycle({
			host,
			reducedMotion: reduced,
			render: renderFrame,
			dispose: () => {
				ro.disconnect();
				host.removeEventListener("pointerenter", enter);
				host.removeEventListener("pointermove", pointer);
				host.removeEventListener("pointerleave", leave);
				renderer.dispose();
			}
		});
	});
};
deferRendererBoot(hosts, boot);